import {
  CONVERSATION_OUTCOMES,
  CONVERSATION_RECORDING_FILTERS,
  ID_PATTERN,
  isoDaySchema,
  type ConversationOutcome,
  type ConversationRecordingFilter,
  type ConversationsQuery,
  type EmailConversationsQuery,
  type WhatsAppConversationsQuery,
} from "@gioia365/contracts";
import { searchParamText } from "../leads/lead-list-hooks";
import type { ConversationsRequest } from "./conversation-queries";

// L'indirizzo di «Conversazioni» (piano M4, Task 19; mock-up `conversazioni.html`): periodo, «Di chi», «Direzione», «Esito»,
// «Owner allora», «Registrazione», «Solo audio ascoltabile» e la ricerca stanno nell'indirizzo (`/conversazioni?periodo=ieri&…`),
// così un link o il ritorno da una scheda riapre la stessa vista. I valori di partenza («Settimana», tutto il team, tutte
// le direzioni, tutti gli esiti e le registrazioni, senza ricerca) non si scrivono; un valore sconosciuto vale come assente.
// «Personalizzato» porta i due giorni, «Dal» e «Al» (`dal`, `al`, `AAAA-MM-GG`): senza tutti e due, o con un giorno che non
// esiste, vale «Settimana»; al contrario si leggono in ordine. Il vecchio `30-giorni` apre «Mese», `7-giorni` la «Settimana».
// Dal piano M6 (R3, passo 12b) anche la scheda, `vista=whatsapp` come nel mock-up (`conversazioni.html?vista=whatsapp`; la scheda
// «Telefonate» è quella di partenza), e «Solo da leggere» dei WhatsApp (`daleggere=1`); dall'ingresso dei Lead (I5, D5) la scheda
// «Email» (`vista=email`). Periodo, «Di chi» e ricerca valgono per tutte le schede, come nel mock-up; i filtri delle sole telefonate
// restano nell'indirizzo ma le altre schede non li usano.

/** I periodi (SEZ-3, più «Tutte»): la parola dell'indirizzo e quella del server. «Settimana» è quello di partenza. */
export const PERIOD_PARAMS = {
  oggi: "today",
  ieri: "yesterday",
  settimana: "last7",
  mese: "last30",
  personalizzato: "custom",
  tutte: "all",
} as const satisfies Record<string, ConversationsQuery["period"]>;
export type PeriodParam = keyof typeof PERIOD_PARAMS;

/** Il periodo di partenza, che l'indirizzo non scrive. */
export const DEFAULT_PERIOD = "settimana" satisfies PeriodParam;

/** Le parole dei link di prima: `30-giorni` è il «Mese» di oggi; `7-giorni` la «Settimana», che non si scrive. */
const OLD_PERIOD_PARAMS: Readonly<Record<string, PeriodParam>> = { "7-giorni": "settimana", "30-giorni": "mese" };

const DIRECTION_PARAMS = { uscita: "outbound", entrata: "inbound" } as const satisfies Record<string, NonNullable<ConversationsQuery["direction"]>>;
type DirectionParam = keyof typeof DIRECTION_PARAMS;

export type ConversationsSearch = {
  /** La scheda: senza, «Telefonate». */
  vista?: "whatsapp" | "email";
  periodo?: Exclude<PeriodParam, typeof DEFAULT_PERIOD>;
  /** «Dal» e «Al» di «Personalizzato» (`AAAA-MM-GG`, «Dal» mai dopo «Al»): ci sono soltanto con quel periodo, tutti e due. */
  dal?: string;
  al?: string;
  /** «Di chi»: chi ha chiamato (la sua appartenenza). Vale soltanto per chi vede le chiamate di tutti. */
  chi?: string;
  direzione?: DirectionParam;
  esito?: ConversationOutcome;
  /** «Owner allora»: l'owner del Lead al momento della chiamata (la sua appartenenza). */
  owner?: string;
  registrazione?: ConversationRecordingFilter;
  /** «Solo audio ascoltabile». */
  audio?: 1;
  /** «Solo da leggere», dei WhatsApp. */
  daleggere?: 1;
  q?: string;
};

const has = <T extends object>(map: T, value: unknown): value is keyof T => typeof value === "string" && Object.hasOwn(map, value);
const isOneOf = <T extends string>(values: readonly T[], value: unknown): value is T => typeof value === "string" && (values as readonly string[]).includes(value);

/** Una persona dell'indirizzo: un identificativo possibile, mai «all» (che è già tutti). La usa anche il Report (`report-search.ts`). */
export function personParam(value: unknown): string | undefined {
  // Un identificativo di sole cifre torna dall'indirizzo come numero (il router legge i valori come JSON).
  const text = typeof value === "number" && Number.isSafeInteger(value) ? String(value) : value;
  return typeof text === "string" && text !== "all" && ID_PATTERN.test(text) ? text : undefined;
}

/** Un giorno dell'indirizzo, `AAAA-MM-GG`, che esiste e che il server accetta (anni 1900–9998); altrimenti nulla. Anche del Report. */
export function dayParam(value: unknown): string | undefined {
  return isoDaySchema.safeParse(value).success ? (value as string) : undefined;
}

/** Il periodo dell'indirizzo, con i due giorni di «Personalizzato» in ordine: senza tutti e due, «Settimana» (nulla da scrivere). */
function periodOf(search: Record<string, unknown>): Pick<ConversationsSearch, "periodo" | "dal" | "al"> {
  const word = typeof search.periodo === "string" ? (OLD_PERIOD_PARAMS[search.periodo] ?? search.periodo) : undefined;
  if (!has(PERIOD_PARAMS, word) || word === DEFAULT_PERIOD) return {};
  if (word !== "personalizzato") return { periodo: word };
  const dal = dayParam(search.dal);
  const al = dayParam(search.al);
  if (dal === undefined || al === undefined) return {};
  // Due giorni `AAAA-MM-GG` si confrontano come testi.
  return dal <= al ? { periodo: word, dal, al } : { periodo: word, dal: al, al: dal };
}

/** I parametri dell'indirizzo `/conversazioni`: un valore sconosciuto o di forma sbagliata vale come assente. */
export function validateConversationsSearch(search: Record<string, unknown>): ConversationsSearch {
  const result: ConversationsSearch = {};
  if (search.vista === "whatsapp" || search.vista === "email") result.vista = search.vista;
  Object.assign(result, periodOf(search));
  const chi = personParam(search.chi);
  if (chi !== undefined) result.chi = chi;
  if (has(DIRECTION_PARAMS, search.direzione)) result.direzione = search.direzione;
  if (isOneOf(CONVERSATION_OUTCOMES, search.esito)) result.esito = search.esito;
  const owner = personParam(search.owner);
  if (owner !== undefined) result.owner = owner;
  if (isOneOf(CONVERSATION_RECORDING_FILTERS, search.registrazione)) result.registrazione = search.registrazione;
  if (search.audio === 1 || search.audio === "1") result.audio = 1;
  if (search.daleggere === 1 || search.daleggere === "1") result.daleggere = 1;
  const q = searchParamText(search.q);
  if (q !== undefined) result.q = q;
  return result;
}

/** La richiesta per l'indirizzo: «Di chi» soltanto per chi vede le chiamate di tutti (agli altri il server lo ignora). */
/** Il periodo per il server: con «Personalizzato» anche `from` e `to`, che `validateConversationsSearch` lascia soltanto lì. */
function periodQueryOf(search: ConversationsSearch): Pick<ConversationsRequest, "period" | "from" | "to"> {
  const period = PERIOD_PARAMS[search.periodo ?? DEFAULT_PERIOD];
  return period === "custom" && search.dal !== undefined && search.al !== undefined ? { period, from: search.dal, to: search.al } : { period };
}

export function conversationsListQueryOf(search: ConversationsSearch, canFilterByPerson: boolean): ConversationsRequest {
  const query: ConversationsRequest = periodQueryOf(search);
  if (canFilterByPerson && search.chi !== undefined) query.who = search.chi;
  if (search.direzione) query.direction = DIRECTION_PARAMS[search.direzione];
  if (search.esito) query.outcome = search.esito;
  if (search.owner !== undefined) query.ownerAtCall = search.owner;
  if (search.registrazione) query.recording = search.registrazione;
  if (search.audio === 1) query.audioOnly = true;
  const q = search.q?.trim();
  if (q) query.q = q;
  return query;
}

/** Che cosa chiedere al server per la scheda «WhatsApp», senza cursore né limite: la lettura delle pagine li aggiunge. */
export type WhatsAppConversationsRequest = Omit<WhatsAppConversationsQuery, "cursor" | "limit">;

/** La richiesta della scheda «WhatsApp» per l'indirizzo: periodo, «Di chi» (soltanto per chi vede tutti), «Solo da leggere», ricerca. */
export function whatsAppListQueryOf(search: ConversationsSearch, canFilterByPerson: boolean): WhatsAppConversationsRequest {
  const query: WhatsAppConversationsRequest = periodQueryOf(search);
  if (canFilterByPerson && search.chi !== undefined) query.who = search.chi;
  if (search.daleggere === 1) query.unread = true;
  const q = search.q?.trim();
  if (q) query.q = q;
  return query;
}

/** Che cosa chiedere al server per la scheda «Email», senza cursore né limite: la lettura delle pagine li aggiunge. */
export type EmailConversationsRequest = Omit<EmailConversationsQuery, "cursor" | "limit">;

/** La richiesta della scheda «Email» per l'indirizzo: periodo, «Di chi» (soltanto per chi vede tutti), ricerca. */
export function emailListQueryOf(search: ConversationsSearch, canFilterByPerson: boolean): EmailConversationsRequest {
  const query: EmailConversationsRequest = periodQueryOf(search);
  if (canFilterByPerson && search.chi !== undefined) query.who = search.chi;
  const q = search.q?.trim();
  if (q) query.q = q;
  return query;
}

/** La direzione dell'indirizzo per la direzione del server, per «Direzione». */
export function directionParamOf(direction: string): DirectionParam | undefined {
  return (Object.keys(DIRECTION_PARAMS) as DirectionParam[]).find((param) => DIRECTION_PARAMS[param] === direction);
}
